import type { Connection, StaticSystem } from "../types/chain";
import { isKspaceClass } from "../types/chain";
import { closestHub } from "./closestHub";

export type StaticLookup = (systemId: string) => StaticSystem | undefined;

export function routeTarget(
  staticLookup: StaticLookup,
  connections: Connection[],
  fromId: string,
): string | null {
  const from = staticLookup(fromId);
  if (!from) return null;
  if (isKspaceClass(from.class)) return fromId;

  const adjacency = new Map<string, string[]>();
  for (const c of connections) {
    if (!adjacency.has(c.from)) adjacency.set(c.from, []);
    if (!adjacency.has(c.to)) adjacency.set(c.to, []);
    adjacency.get(c.from)!.push(c.to);
    adjacency.get(c.to)!.push(c.from);
  }

  const distance = new Map<string, number>([[fromId, 0]]);
  const queue: string[] = [fromId];
  let bestDistance = Infinity;
  const candidates: StaticSystem[] = [];
  while (queue.length > 0) {
    const node = queue.shift()!;
    const d = distance.get(node)!;
    if (d > bestDistance) break;
    const system = staticLookup(node);
    if (system && node !== fromId && isKspaceClass(system.class)) {
      bestDistance = d;
      candidates.push(system);
    }
    for (const neighbor of adjacency.get(node) ?? []) {
      if (!distance.has(neighbor)) {
        distance.set(neighbor, d + 1);
        queue.push(neighbor);
      }
    }
  }

  if (candidates.length === 0) return null;

  const ranked = candidates
    .map((s) => ({
      id: s.id,
      hubJumps: closestHub(s.hub_jumps)?.jumps ?? Infinity,
    }))
    .sort((a, b) => a.hubJumps - b.hubJumps || Number(a.id) - Number(b.id));
  return ranked[0].id;
}
